import { baseApi } from './baseApi';

import type { CurrencyCode } from '../../utils/moneda';
export type IssueSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';
export type IssueStatus   = 'open' | 'investigating' | 'resolved';
export type IssueAudience = 'all' | 'agents' | 'notaries' | 'office_admins';

export interface PlatformIssue {
  _id:         string;
  title:       string;
  description: string;
  severity:    IssueSeverity;
  status:      IssueStatus;
  audience:    IssueAudience;
  notifyInApp: boolean;
  notifiedAt?: string;
  createdAt:   string;
  updatedAt:   string;
  resolvedAt?: string;
}

/** `waiting_user`: se le pidió un dato a quien reportó y sin eso no se avanza.
 *  Separado de `in_review` para distinguir "lo estamos trabajando" de "esperamos al agente".
 *
 *  `fixed_pending_push`: arreglado y probado, pero SIN subir todavía — el agente lo sigue
 *  viendo roto. Marcarlo resuelto antes de tiempo hace que alguien lo lea y crea que ya
 *  puede probarlo. (06/08/2026) */
export type BugReportStatus   = 'new' | 'in_review' | 'waiting_user' | 'fixed_pending_push' | 'resolved' | 'dismissed' | 'auto_closed';
export type BugAttachmentType = 'screenshot' | 'video';
export type BugSeverity       = 'low' | 'medium' | 'high' | 'critical';
export type BugPriority       = 'p1' | 'p2' | 'p3' | 'p4';
export type BugReportKind     = 'bug' | 'suggestion';

export interface BugAttachment {
  type: BugAttachmentType;
  url:  string;
  key:  string;
  size: number;
}

export interface BugReport {
  _id:          string;
  userId:       string;
  userName:     string;
  userRole:     string;
  url:          string;
  kind?:        BugReportKind;
  description:  string;
  userAgent?:   string;
  sessionId?:   string;
  attachments:  BugAttachment[];
  status:       BugReportStatus;
  severity?:    BugSeverity;
  priority?:    BugPriority;
  tags?:        string[];
  adminNote?:   string;
  /** Canal de entrada: 'gustavo' = promovido desde el monitor de IA. */
  origin?:      string;
  /** Llave de intake automático (golden:<id> | incident:<id>). */
  sourceMessageId?: string;
  /** Instrumento del fix (test:<archivo.test.ts> | golden:<id|nombre>). Obligatorio para resolver bugs de Gustavo. */
  instrumentRef?: string;
  responseToUser?: string;
  responseSentAt?: string;
  resolvedAt?:  string;
  createdAt:    string;
  updatedAt:    string;
}

export type AnalyticsTimelineType =
  | 'page_view' | 'action' | 'property_view' | 'search' | 'gustavo_chat'
  | 'gustavo_tool' | 'cta_click' | 'funnel_step' | 'error' | 'auth';

export interface AnalyticsTimelineEvent {
  _id:           string;
  type:          AnalyticsTimelineType;
  category:      'agent' | 'client' | 'system';
  action:        string;
  label?:        string;
  path?:         string;
  resourceType?: string;
  resourceId?:   string;
  meta?:         Record<string, unknown>;
  createdAt:     string;
}

export interface BugTimelineResponse {
  bugReport: {
    _id: string; userId: string; userName: string;
    sessionId: string | null; url: string; description: string; createdAt: string;
  };
  window:    { since: string; until: string; minutesBefore: number };
  filter:    'sessionId' | 'userId';
  total:     number;
  truncated: boolean;
  events:    AnalyticsTimelineEvent[];
}

interface CreateIssueBody {
  title:        string;
  description?: string;
  severity?:    IssueSeverity;
  status?:      IssueStatus;
  audience?:    IssueAudience;
  notifyInApp?: boolean;
}

interface UpdateIssueBody extends Partial<CreateIssueBody> {}

interface CreateBugReportBody {
  url:          string;
  kind?:        BugReportKind;
  description:  string;
  userAgent?:   string;
  sessionId?:   string;
  attachments?: BugAttachment[];
  tags?:        string[];
  /** Reportes creados desde el monitor de Gustavo. */
  origin?:      'form' | 'chat' | 'loop' | 'gustavo';
  /** Clave idempotente: evita duplicar el bug si el backend ya reportó el mismo turno. */
  sourceMessageId?: string;
}

/** Una sugerencia ya implementada — feed "Lo que pediste, lo hicimos". */
export interface ImplementedSuggestion {
  _id:            string;
  description:    string;
  responseToUser?: string;
  resolvedAt?:    string;
  proposerName:   string;
  isMine:         boolean;
  tags?:          string[];
}

interface UpdateBugReportBody {
  status?:    BugReportStatus;
  severity?:  BugSeverity;
  priority?:  BugPriority;
  tags?:      string[];
  adminNote?: string;
  instrumentRef?: string;
}

interface RespondToBugBody {
  message:    string;
  newStatus?: 'in_review' | 'resolved' | 'dismissed';
  instrumentRef?: string;
}

export interface AiUsageRow {
  key: string;
  kind: 'agent' | 'client';
  label: string;
  office?: string;
  costUsd: number;
  promptTokens: number;
  cachedTokens: number;
  completionTokens: number;
  requests: number;
  flowExecutions: number;
  bySourceCost: Record<string, number>;
  /** Costo convertido a créditos (1 crédito = 1 peso uruguayo, vía BCU). null si no hay cotización. */
  credits: number | null;
}
export interface GustavoTopAgent {
  userId:   string;
  name:     string;
  avatar:   string | null;
  office:   string | null;
  /** Turnos totales del agente con Gustavo en el período. */
  total:    number;
  /** Cuántos de esos turnos entraron por WhatsApp. */
  whatsapp: number;
  /** Cuántos por el chat del CRM. */
  crm:      number;
  costUsd:  number;
  lastAt:   string;
}

export interface GustavoTopAgents {
  from:   string;
  days:   number;
  /** Totales de TODOS los agentes del período, no sólo de los del top. */
  totals: {
    whatsapp:    number;
    crm:         number;
    total:       number;
    costUsd:     number;
    /** Cuántos agentes le escribieron a Gustavo en el período. */
    agentsCount: number;
  };
  agents: GustavoTopAgent[];
}

/** Consumo de CRÉDITOS de Gustavo del mes en curso, por agente y canal. */
export interface GustavoCreditsAgent {
  userId:   string;
  name:     string;
  avatar:   string | null;
  creditos: number;
  whatsapp: number;
  web:      number;
  mensajes: number;
  /** % de la cuota mensual ya gastado. Puede pasar de 100. */
  pctQuota: number;
  lastAt:   string;
  /** Cuándo se quedó sin créditos. `null` = todavía le quedan. */
  sinCreditosAt: string | null;
  /** Cuántos días del mes le duró el paquete. `null` si no lo agotó. */
  diasQueDuro:   number | null;
}
export interface GustavoCredits {
  from:     string;
  /** Mes en curso, ya formateado (ej. "julio de 2026"). */
  periodo:  string;
  minCredits: number;
  /** Cuota mensual por agente (Imperium: 300, no acumulativa). */
  quotaPerAgent: number;
  totals: {
    creditos:      number;
    whatsapp:      number;
    web:           number;
    mensajes:      number;
    agentsCount:   number;
    overThreshold: number;
    overQuota:     number;
  };
  agents: GustavoCreditsAgent[];
}

/** Una fila = un modelo en un día del mes en curso. */
export interface ConsumoDiaModelo {
  dia: string;            // 'YYYY-MM-DD' (hora Uruguay)
  /** Margen con el que se cobró ESE día. Puede no ser el de hoy. */
  margen: number;
  modelo: string;
  turnos: number;
  costUsd: number;
  creditos: number;
}
export interface ConsumoPorModelo {
  mes: string;            // 'YYYY-MM'
  hoy: string;            // 'YYYY-MM-DD'
  /** Con qué versión de Gustavo se generó este consumo, y sobre cuánta gente. */
  gustavo: { version: string; alcance: string };
  /**
   * Los sistemas que están ATENDIENDO, con sus modelos. Casi siempre es uno; son dos
   * mientras conviven. Quién entra lo decide el backend contando oficinas
   * (`sistemasEnUso`): un sistema que ya no atiende a nadie NO viene, aunque la flag siga
   * en `targeted`. Los modelos de `modelos` que ningún sistema declara son historia del
   * mes — consumieron y ya no corren.
   */
  sistemas?: Array<{
    version: string; alcance: string;
    base: string; complejos: string;
    api?: 'responses' | 'chat-completions';
  }>;
  /** Margen sobre el costo real, HOY. `null` para el admin de un tenant (no lo ve). */
  multiplicadorCreditos: number | null;
  /** false = la respuesta viene sin costos ni margen (vista de un tenant). */
  incluyeCosto: boolean;
  /** Tramos de margen que tocan el mes: `diaDelMes` no nulo = cambió ese día. */
  vigenciasMargen: Array<{ desde: string; valor: number; motivo: string; diaDelMes: number | null }>;
  diasDelMes: number;
  /** Día del mes en curso: hasta acá hay datos, de acá en adelante va el gris. */
  diaActual: number;
  /** Ordenados por gasto del mes: fija el color de cada modelo en todo el widget. */
  modelos: string[];
  filas: ConsumoDiaModelo[];
  totales: { turnos: number; costUsd: number; creditos: number };
}

export interface AiUsageSummary {
  from: string;
  to: string;
  totals: {
    costUsd: number; promptTokens: number; cachedTokens: number;
    completionTokens: number; requests: number; flowExecutions: number;
    credits: number | null;
  };
  /** Cotización BCU usada para convertir USD → créditos. */
  fxRate: { usdToUyu: number; fetchedAt: string; isStale: boolean } | null;
  /** Pesos uruguayos por crédito (valor al cliente). créditos = costoUSD × cotización ÷ este valor. */
  creditValueUyu: number;
  byOffice: AiUsageOffice[];
  rows: AiUsageRow[];
}
export interface AiUsageOffice {
  officeId: string | null;
  label: string;
  costUsd: number;
  requests: number;
  flowExecutions: number;
  promptTokens: number;
  completionTokens: number;
  credits: number | null;
}

// ─── Encuestas ────────────────────────────────────────────────────────────────
export type SurveyStatus = 'draft' | 'active' | 'closed';
export interface SurveyQuestion {
  id: string; type: 'nps' | 'scale' | 'text' | 'yesno'; label: string; max?: number;
  /** Para 'yesno': el texto de los dos botones (default "Sí" / "No"). */
  labels?: [string, string];
}
export interface Survey {
  _id: string;
  title: string;
  description?: string;
  audience: 'all' | 'agents' | 'notaries' | 'office_admins';
  /** Oficina destinataria. null = toda la red. */
  officeId?: string | null;
  questions: SurveyQuestion[];
  /** 'floating' = tarjeta flotante con las preguntas a la vista; 'banner' = el aviso de siempre. */
  display?: 'banner' | 'floating';
  status: SurveyStatus;
  responseCount?: number;
  activatedAt?: string;
  createdAt: string;
}
export interface SurveyAnswer { questionId: string; value: number | string | null; }
export interface SurveyResultQuestion extends SurveyQuestion {
  count?: number; avg?: number | null; nps?: number | null;
  promoters?: number; passives?: number; detractors?: number;
  /** Con autor: un comentario suelto no se puede ir a conversar con nadie. */
  comments?: Array<{ texto: string; autor: string }>;
}

/** Una respuesta, con nombre y el valor de cada pregunta. */
export interface SurveyRespuesta {
  userId:  string;
  nombre:  string;
  oficina: string | null;
  fecha:   string | null;
  /** questionId → lo que contestó. */
  valores: Record<string, number | string | null>;
}
export interface SurveyParticipant { userId: string; name: string; }
export interface SurveyOfficeParticipation {
  officeId: string | null;
  officeName: string;
  responded: SurveyParticipant[];
  pending: SurveyParticipant[];
  respondedCount: number;
  totalCount: number;
}
export interface SurveyResults {
  survey: Survey;
  total: number;
  questions: SurveyResultQuestion[];
  respuestas?: SurveyRespuesta[];
  participation?: SurveyOfficeParticipation[];
  eligibleTotal?: number;
  respondedTotal?: number;
  tenantId?: string;
}

/** Cómo se ve la suscripción de un tenant en la lista (backend `estadoDeCobro`). */
export interface EstadoDeCobro {
  etiqueta: string;
  tono: 'ok' | 'aviso' | 'alerta' | 'neutro';
  /** Días hasta el próximo cobro; negativo = vencido. */
  dias: number | null;
  detalle: string;
}

export interface PlatformTenant {
  _id: string;
  name: string;
  slug: string;
  mode: 'native' | 'booster';
  status: string;
  /** Plan contratado. `null` en el nativo (Imperium no paga suscripción). */
  plan?: string | null;
  /** Estado de la suscripción (trial / active / …). `null` en el nativo. */
  planStatus?: string | null;
  priceUsd?: number | null;
  /** Marca: logo, foto de la cuenta y color propio para el avatar. */
  logoUrl?: string | null;
  fotoUrl?: string | null;
  brandColor?: string | null;
  country?: string | null;
  isDemo?: boolean;
  createdAt?: string;
  /** Resumen para la LISTA. La ficha del detalle trae `cobro`, que es otra cosa. */
  cobroResumen?: EstadoDeCobro | null;
  ultimoPagoAt?: string | null;
  metrics?: { offices: number; users: number; activeAgents: number; customers: number; properties: number; activeProperties: number };
}

/** Respuesta de "entrar a un tenant": el tenant + su contexto (modo/plan/branding),
 *  para que el front renderice el CRM de ESE tenant en modo soporte. */
export interface EnterTenantResult extends PlatformTenant {
  tenantMode?: 'native' | 'booster';
  native?: boolean;
  features?: string[];
  branding?: { logoUrl?: string; primaryColor?: string; displayName?: string } | null;
  /** Identidad emulada del admin del tenant → el menú se pinta como su login. */
  role?: string;
  permissions?: string[];
}

export interface TenantPayment {
  _id: string;
  amount: number;
  currency: CurrencyCode;
  status: string;
  card?: { brand?: string; last4?: string };
  relatedKind: string;
  createdAt: string;
  paidAt?: string | null;
  description?: string;
  paymentMethod?: string;
  providerPaymentId?: string;
}
/** La ficha de cobro (backend `fichaDeCobro`): cuándo pagó, cuándo le toca, con qué. */
export interface TenantCobro {
  estado: string | null;
  periodoDesde: string | null;
  periodoHasta: string | null;
  proximoCobro: string | null;
  cancelaAlCierre: boolean;
  finDePrueba: string | null;
  ultimoCobro: string | null;
  ultimoPago: { fecha: string; monto: number; moneda: string } | null;
  puedeCobrarSola: boolean;
  tarjeta: { brand: string | null; last4: string | null } | null;
  asientos: number | null;
  adicionalMensualUsd: number;
  cambioDePlanPendiente: boolean;
}
export interface TenantAdmin { _id: string; name: string; lastName?: string; email: string; phone?: string; isActive: boolean; role?: string }

/** ¿La están usando? (backend `saludDelTenant`). */
export interface TenantActividad {
  salud: { etiqueta: string; tono: 'ok' | 'aviso' | 'alerta' | 'neutro'; diasSinEntrar: number | null };
  ultimoAccesoAt: string | null;
  ultimoAccesoPor: string | null;
  ultimaPropiedadAt: string | null;
  ultimoClienteAt: string | null;
  propiedades30d: number;
  clientes30d: number;
}
/** Uso contra los topes del plan. `tope: null` = sin límite. */
export interface TenantUso {
  usuarios:    { usado: number; tope: number | null };
  propiedades: { usado: number; tope: number | null };
  creditos:    { usado: number; tope: number | null };
}
export interface TenantSitio {
  subdomain: string | null;
  customDomain: string | null;
  customDomainStatus: string | null;
  publicado: boolean;
}
export interface TenantSubscriptionInfo {
  native: boolean;
  active: boolean;
  status: string;
  tier?: string;
  planName?: string;
  priceUsd?: number;
  renewsAt?: string;
  features?: string[];
  gustavoUsed?: number;
  limits?: { users?: number | null; properties?: number | null; gustavoMonthlyCredits?: number | null; gustavoCreditsRenuevan?: boolean };
}
export interface TenantDetail extends PlatformTenant {
  createdAt: string;
  country?: string;
  isDemo?: boolean;
  subscription: TenantSubscriptionInfo | null;
  payments: TenantPayment[];
  cobro?: TenantCobro | null;
  usuarioDeCobro?: { name: string; lastName?: string; email: string } | null;
  admins?: TenantAdmin[];
  actividad?: TenantActividad;
  uso?: TenantUso | null;
  oficinas?: Array<{ _id: string; name: string }>;
  sitio?: TenantSitio | null;
}

export interface PlatformPlan {
  _id: string;
  key: string;
  name: string;
  tier: string;
  priceUsd: number | null;
  custom?: boolean;
  limits?: { users?: number | null; properties?: number | null };
}
export interface PlatformCoupon {
  _id: string;
  code: string;
  /** `plan` = días gratis (los de siempre) · `marketplace` = % de descuento al comprar. Ausente = plan. */
  kind?: 'plan' | 'marketplace';
  planKey?: string;
  freeDays?: number;
  productSku?: string;
  discountPct?: number;
  maxUses?: number;
  usedCount?: number;
  used: boolean;
  usedByCompany?: string;
  usedAt?: string;
  expiresAt?: string;
  active: boolean;
  note?: string;
  createdAt: string;
}
export interface CreateTenantResult {
  tenantId: string;
  adminEmail: string;
  tempPassword: string;
}

export interface PlatformSignup {
  _id: string;
  companyName: string;
  planKey: string;
  adminEmail: string;
  status: 'pending_payment' | 'provisioned' | 'failed';
  tenantId?: string;
  createdAt: string;
}

export interface PlatformAccessEntry {
  _id: string;
  actorEmail: string;
  actorRole: string;
  action: 'enter' | 'exit';
  tenantName?: string;
  tenantSlug?: string;
  createdAt: string;
}

/**
 * Actividad de reportes de la oficina — lo que ve el admin de oficina.
 *
 * Deliberadamente NO trae el texto del reporte ni los adjuntos: la pregunta que
 * contesta es "quién reporta", no "qué dijeron". Si el agente supiera que su jefe lee
 * el contenido, reportaría menos. Del texto sale sólo un título derivado.
 */
export interface OfficeBugActivity {
  resumen: {
    total: number;
    bugs: number;
    sugerencias: number;
    /** Personas distintas que reportaron alguna vez. */
    personas: number;
    /** Total de gente activa en el alcance (oficina o inmobiliaria). */
    miembros: number;
    sinReportar: number;
  };
  porPersona: Array<{
    userId: string;
    nombre: string;
    rol: string;
    total: number;
    bugs: number;
    sugerencias: number;
    ultimo: string | null;
  }>;
  /** Los que nunca reportaron: la otra mitad de la pregunta del admin. */
  sinReportar: Array<{ userId: string; nombre: string; rol: string }>;
  reportes: Array<{
    id: string;
    quien: string;
    rol: string;
    fecha: string;
    tipo: 'bug' | 'suggestion';
    estado: string;
    titulo: string;
  }>;
}

/**
 * Detalle de un reporte del equipo. Trae el texto completo y la respuesta de soporte;
 * los adjuntos quedan fuera a propósito (las capturas son de la pantalla del agente y
 * suelen tener fichas de clientes que no son suyos).
 */
export interface OfficeBugDetail {
  id: string;
  quien: string;
  rol: string;
  fecha: string;
  tipo: 'bug' | 'suggestion';
  estado: string;
  titulo: string;
  descripcion: string;
  /** Ruta del CRM donde estaba parado al reportar, ej. "/captaciones". */
  pantalla: string | null;
  respuestaSoporte: string | null;
  respondidoEl: string | null;
  resueltoEl: string | null;
}

/** Filtros del inbox de reportes. Van al SERVIDOR: son 811 y sólo crecen. */
export interface BugReportsQuery {
  status?:     BugReportStatus;
  /** Atajo de la pantalla que separa Abiertos de Resueltos. Abierto = new | in_review. */
  abiertos?:   boolean;
  severity?:   BugSeverity;
  attachment?: 'screenshot' | 'video';
  page?:       number;   // 1-based, como lo devuelve el backend
  limit?:      number;
}

export interface BugReportsPage {
  rows:   BugReport[];
  total:  number;
  page:   number;
  limit:  number;
  pages:  number;
  /**
   * Por estado y sobre TODO el universo, no sobre la página: son los números de las
   * pestañas. Si salieran de la página dirían lo que hay en pantalla, no lo que hay.
   */
  counts: Record<BugReportStatus, number>;
}

export const platformApi = baseApi.injectEndpoints({
  endpoints: (build) => ({

    // ─── Issues ─────────────────────────────────────────────────
    listOpenIssues: build.query<PlatformIssue[], void>({
      query: () => '/platform/issues',
      transformResponse: (res: { data: PlatformIssue[] }) => res.data,
      providesTags: ['PlatformIssue'],
    }),

    listAllIssues: build.query<PlatformIssue[], void>({
      query: () => '/platform/issues/all',
      transformResponse: (res: { data: PlatformIssue[] }) => res.data,
      providesTags: ['PlatformIssue'],
    }),

    createIssue: build.mutation<PlatformIssue, CreateIssueBody>({
      query: (body) => ({ url: '/platform/issues', method: 'POST', body }),
      transformResponse: (res: { data: PlatformIssue }) => res.data,
      invalidatesTags: ['PlatformIssue'],
    }),

    updateIssue: build.mutation<PlatformIssue, { id: string; body: UpdateIssueBody }>({
      query: ({ id, body }) => ({ url: `/platform/issues/${id}`, method: 'PATCH', body }),
      transformResponse: (res: { data: PlatformIssue }) => res.data,
      invalidatesTags: ['PlatformIssue'],
    }),

    deleteIssue: build.mutation<void, string>({
      query: (id) => ({ url: `/platform/issues/${id}`, method: 'DELETE' }),
      invalidatesTags: ['PlatformIssue'],
    }),

    // ─── Bug reports ────────────────────────────────────────────
    uploadBugAttachment: build.mutation<BugAttachment, FormData>({
      query: (formData) => ({
        url:    '/platform/bug-reports/upload-attachment',
        method: 'POST',
        body:   formData,
      }),
      transformResponse: (res: { data: BugAttachment }) => res.data,
    }),

    createBugReport: build.mutation<BugReport, CreateBugReportBody>({
      query: (body) => ({ url: '/platform/bug-reports', method: 'POST', body }),
      transformResponse: (res: { data: BugReport }) => res.data,
      invalidatesTags: ['BugReport'],
    }),

    listBugReports: build.query<BugReportsPage, BugReportsQuery | void>({
      query: (q) => {
        const p = new URLSearchParams();
        if (q?.status)     p.set('status', q.status);
        else if (q?.abiertos !== undefined) p.set('abiertos', String(q.abiertos));
        if (q?.severity)   p.set('severity', q.severity);
        if (q?.attachment) p.set('attachment', q.attachment);
        p.set('page',  String(q?.page  ?? 1));
        p.set('limit', String(q?.limit ?? 20));
        return `/platform/bug-reports?${p.toString()}`;
      },
      transformResponse: (res: { data: BugReportsPage }) => res.data,
      providesTags: ['BugReport'],
    }),

    listMyBugReports: build.query<BugReport[], void>({
      query: () => '/platform/bug-reports/mine',
      transformResponse: (res: { data: BugReport[] }) => res.data,
      providesTags: ['BugReport'],
    }),

    getOfficeBugActivity: build.query<OfficeBugActivity, void>({
      query: () => '/platform/bug-reports/office-activity',
      transformResponse: (res: { data: OfficeBugActivity }) => res.data,
      providesTags: ['BugReport'],
    }),

    getOfficeBugDetail: build.query<OfficeBugDetail, string>({
      query: (id) => `/platform/bug-reports/office-activity/${id}`,
      transformResponse: (res: { data: OfficeBugDetail }) => res.data,
      providesTags: ['BugReport'],
    }),

    listImplementedSuggestions: build.query<ImplementedSuggestion[], void>({
      query: () => '/platform/bug-reports/implemented-suggestions',
      transformResponse: (res: { data: ImplementedSuggestion[] }) => res.data,
      providesTags: ['BugReport'],
    }),

    listUrgentBugReports: build.query<BugReport[], { limit?: number } | void>({
      query: (arg) => `/platform/bug-reports/urgent${arg?.limit ? `?limit=${arg.limit}` : ''}`,
      transformResponse: (res: { data: BugReport[] }) => res.data,
      providesTags: ['BugReport'],
    }),

    respondToBugReport: build.mutation<BugReport, { id: string; body: RespondToBugBody }>({
      query: ({ id, body }) => ({ url: `/platform/bug-reports/${id}/respond`, method: 'POST', body }),
      transformResponse: (res: { data: BugReport }) => res.data,
      invalidatesTags: ['BugReport'],
    }),

    getBugReportTimeline: build.query<BugTimelineResponse, { id: string; window?: number; limit?: number }>({
      query: ({ id, window, limit }) => {
        const qs = new URLSearchParams();
        if (window) qs.set('window', String(window));
        if (limit)  qs.set('limit',  String(limit));
        const q = qs.toString();
        return `/platform/bug-reports/${id}/timeline${q ? `?${q}` : ''}`;
      },
      transformResponse: (res: { data: BugTimelineResponse }) => res.data,
    }),

    updateBugReport: build.mutation<BugReport, { id: string; body: UpdateBugReportBody }>({
      query: ({ id, body }) => ({ url: `/platform/bug-reports/${id}`, method: 'PATCH', body }),
      transformResponse: (res: { data: BugReport }) => res.data,
      invalidatesTags: ['BugReport'],
    }),

    /** Top de agentes por cantidad de turnos con Gustavo, partido por canal. */
    getGustavoTopAgents: build.query<GustavoTopAgents, { days?: number; limit?: number } | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.days)  qs.set('days',  String(params.days));
        if (params?.limit) qs.set('limit', String(params.limit));
        const q = qs.toString();
        return `/ai-usage/top-agents${q ? `?${q}` : ''}`;
      },
      transformResponse: (res: { data: GustavoTopAgents }) => res.data,
    }),

    getGustavoCredits: build.query<GustavoCredits, { minCredits?: number; limit?: number } | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.minCredits != null) qs.set('minCredits', String(params.minCredits));
        if (params?.limit)              qs.set('limit', String(params.limit));
        const q = qs.toString();
        return `/ai-usage/gustavo-credits${q ? `?${q}` : ''}`;
      },
      transformResponse: (res: { data: GustavoCredits }) => res.data,
    }),

    /** Consumo por modelo y por día del mes en curso (widget de la home de soporte). */
    getAiConsumoPorModelo: build.query<ConsumoPorModelo, void>({
      query: () => '/ai-usage/por-modelo',
      transformResponse: (res: { data: ConsumoPorModelo }) => res.data,
    }),

    getAiUsage: build.query<AiUsageSummary, { from?: string; to?: string } | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.from) qs.set('from', params.from);
        if (params?.to)   qs.set('to',   params.to);
        const q = qs.toString();
        return `/ai-usage${q ? `?${q}` : ''}`;
      },
      transformResponse: (res: { data: AiUsageSummary }) => res.data,
    }),

    // ─── Encuestas ──────────────────────────────────────────────
    getActiveSurvey: build.query<{ survey: Survey | null }, void>({
      query: () => '/platform/surveys/active',
      transformResponse: (res: { data: { survey: Survey | null } }) => res.data,
      providesTags: ['SurveyActive'],
    }),
    getSurveyForMe: build.query<{ survey: Survey; answered: boolean }, string>({
      query: (id) => `/platform/surveys/${id}/me`,
      transformResponse: (res: { data: { survey: Survey; answered: boolean } }) => res.data,
      providesTags: ['SurveyActive'],
    }),
    respondSurvey: build.mutation<{ ok: boolean }, { id: string; answers: SurveyAnswer[] }>({
      query: ({ id, answers }) => ({ url: `/platform/surveys/${id}/respond`, method: 'POST', body: { answers } }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
      invalidatesTags: ['SurveyActive'],
    }),
    listSurveys: build.query<Survey[], void>({
      query: () => '/platform/surveys',
      transformResponse: (res: { data: Survey[] }) => res.data,
      providesTags: ['Survey'],
    }),
    createSurvey: build.mutation<Survey, { title: string; description?: string; audience?: string; questions?: SurveyQuestion[]; officeId?: string | null }>({
      query: (body) => ({ url: '/platform/surveys', method: 'POST', body }),
      transformResponse: (res: { data: Survey }) => res.data,
      invalidatesTags: ['Survey'],
    }),
    deleteSurvey: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/platform/surveys/${id}`, method: 'DELETE' }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
      invalidatesTags: ['Survey'],
    }),
    updateSurvey: build.mutation<Survey, { id: string; body: { status?: SurveyStatus; title?: string; description?: string; questions?: SurveyQuestion[]; audience?: string; officeId?: string | null } }>({
      query: ({ id, body }) => ({ url: `/platform/surveys/${id}`, method: 'PATCH', body }),
      transformResponse: (res: { data: Survey }) => res.data,
      invalidatesTags: ['Survey', 'SurveyActive'],
    }),
    getSurveyResults: build.query<SurveyResults, { id: string; tenantId?: string }>({
      query: ({ id, tenantId }) => ({
        url: `/platform/surveys/${id}/results`,
        params: tenantId ? { tenantId } : undefined,
      }),
      transformResponse: (res: { data: SurveyResults }) => res.data,
      providesTags: ['Survey'],
    }),

    // ─── Acceso cross-tenant (operador/soporte): "entrar a un tenant" ───
    listPlatformTenants: build.query<PlatformTenant[], void>({
      query: () => '/platform/tenants',
      transformResponse: (res: { data: PlatformTenant[] }) => res.data,
    }),
    enterTenant: build.mutation<EnterTenantResult, string>({
      query: (tenantId) => ({ url: '/platform/enter-tenant', method: 'POST', body: { tenantId } }),
      transformResponse: (res: { data: EnterTenantResult }) => res.data,
    }),
    exitTenant: build.mutation<{ ok: boolean }, void>({
      query: () => ({ url: '/platform/exit-tenant', method: 'POST' }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
    }),
    getPlatformAccessLog: build.query<PlatformAccessEntry[], void>({
      query: () => '/platform/access-log',
      transformResponse: (res: { data: PlatformAccessEntry[] }) => res.data,
    }),
    listPlatformSignups: build.query<PlatformSignup[], void>({
      query: () => '/platform/signups',
      transformResponse: (res: { data: PlatformSignup[] }) => res.data,
    }),
    getTenantDetail: build.query<TenantDetail, string>({
      query: (id) => `/platform/tenants/${id}`,
      transformResponse: (res: { data: TenantDetail }) => res.data,
    }),
    setTenantStatus: build.mutation<{ _id: string; status: string }, { id: string; status: 'active' | 'suspended' }>({
      query: ({ id, status }) => ({ url: `/platform/tenants/${id}/status`, method: 'PATCH', body: { status } }),
      transformResponse: (res: { data: { _id: string; status: string } }) => res.data,
    }),
    listPlatformPlans: build.query<PlatformPlan[], void>({
      query: () => '/platform/plans',
      transformResponse: (res: { data: PlatformPlan[] }) => res.data,
    }),
    createTenant: build.mutation<CreateTenantResult, {
      companyName: string; country: string; adminName: string; adminLastName?: string;
      adminEmail: string; adminPhone?: string; planKey: string; trialDays?: number;
    }>({
      query: (body) => ({ url: '/platform/tenants', method: 'POST', body }),
      transformResponse: (res: { data: CreateTenantResult }) => res.data,
    }),
    listCoupons: build.query<PlatformCoupon[], void>({
      query: () => '/platform/coupons',
      transformResponse: (res: { data: PlatformCoupon[] }) => res.data,
      providesTags: ['Coupon'],
    }),
    createCoupon: build.mutation<PlatformCoupon, {
      kind?: 'plan' | 'marketplace';
      planKey?: string; freeDays?: number;
      productSku?: string; discountPct?: number; maxUses?: number;
      expiresAt?: string; note?: string; tenantId?: string;
    }>({
      query: (body) => ({ url: '/platform/coupons', method: 'POST', body }),
      transformResponse: (res: { data: PlatformCoupon }) => res.data,
      invalidatesTags: ['Coupon'],
    }),
    deleteCoupon: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/platform/coupons/${id}`, method: 'DELETE' }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
      invalidatesTags: ['Coupon'],
    }),
    applyCoupon: build.mutation<
      { ok: boolean; tenant: string; code: string; plan: string; freeDays: number; trialEndsAt: string },
      { tenantId: string; couponId: string }
    >({
      query: ({ tenantId, couponId }) => ({ url: `/platform/tenants/${tenantId}/apply-coupon`, method: 'POST', body: { couponId } }),
      transformResponse: (res: { data: { ok: boolean; tenant: string; code: string; plan: string; freeDays: number; trialEndsAt: string } }) => res.data,
      invalidatesTags: ['Coupon'],
    }),
  }),
});

export const {
  useListOpenIssuesQuery,
  useListAllIssuesQuery,
  useCreateIssueMutation,
  useUpdateIssueMutation,
  useDeleteIssueMutation,
  useUploadBugAttachmentMutation,
  useCreateBugReportMutation,
  useListBugReportsQuery,
  useListMyBugReportsQuery,
  useGetOfficeBugActivityQuery,
  useGetOfficeBugDetailQuery,
  useListImplementedSuggestionsQuery,
  useListUrgentBugReportsQuery,
  useUpdateBugReportMutation,
  useRespondToBugReportMutation,
  useLazyGetBugReportTimelineQuery,
  useGetAiUsageQuery,
  useGetAiConsumoPorModeloQuery,
  useGetGustavoTopAgentsQuery,
  useGetGustavoCreditsQuery,
  useGetActiveSurveyQuery,
  useGetSurveyForMeQuery,
  useRespondSurveyMutation,
  useListSurveysQuery,
  useCreateSurveyMutation,
  useUpdateSurveyMutation,
  useDeleteSurveyMutation,
  useGetSurveyResultsQuery,
  useListPlatformTenantsQuery,
  useEnterTenantMutation,
  useExitTenantMutation,
  useGetPlatformAccessLogQuery,
  useListPlatformSignupsQuery,
  useGetTenantDetailQuery,
  useSetTenantStatusMutation,
  useListPlatformPlansQuery,
  useCreateTenantMutation,
  useListCouponsQuery,
  useCreateCouponMutation,
  useDeleteCouponMutation,
  useApplyCouponMutation,
} = platformApi;
